/**
 * «Equipo en ejecución» (BHM-CLD-REQ-045a) — Dirección (solo SUPERADMIN, lo exige la API).
 * Por empleado: estado de la jornada, tarea actual o última, resultado y siguiente prevista.
 * Con JORNADA FINALIZADA se conserva la última tarea, su resultado y la siguiente.
 */
import { useEffect, useState } from 'react';
import { activityApi, errText, type TeamLive } from '../../stores/activityApi';
import { Icon, Pill, ErrorBanner } from './WorkUi';
import { closeDecisionText, teamPillCls, teamStateText } from './flowLogic';

export default function TeamLivePanel() {
  const [data, setData] = useState<TeamLive | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    activityApi.team().then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError(errText(e, 'No se pudo cargar el equipo en ejecución')); });
    return () => { alive = false; };
  }, []);

  return (
    <section className="rounded-xl border border-outline-variant bg-surface-container-low p-5 xl:col-span-2" data-testid="team-live" aria-label="Equipo en ejecución">
      <h2 className="mb-3 flex items-center gap-2 font-title-md text-on-surface"><Icon name="groups" className="text-on-surface-variant" />Equipo en ejecución</h2>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!data && !error && <p className="text-sm text-on-surface-variant">Cargando…</p>}
      {data && (data.employees.length === 0 ? <p className="py-3 text-sm text-on-surface-variant/70">Sin empleados activos.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wider text-on-surface-variant">
              <tr><th className="py-1 pr-3">Empleado</th><th className="py-1 pr-3">Estado</th><th className="py-1 pr-3">Ahora / última tarea</th><th className="py-1 pr-3">Resultado</th><th className="py-1">Siguiente</th></tr>
            </thead>
            <tbody>
              {data.employees.map((r) => {
                const decision = r.workday.state === 'FINISHED' ? closeDecisionText(r.closeDecision) : null;
                return (
                  <tr key={r.userId} className="border-t border-outline-variant/60 align-top" data-testid="team-row" data-state={r.label}>
                    <td className="py-2 pr-3 text-on-surface">{r.name}</td>
                    <td className="py-2 pr-3">
                      <Pill className={teamPillCls(r.label)}>{teamStateText(r)}</Pill>
                      {decision && <span className="mt-1 block text-[11px] text-on-surface-variant">{decision}</span>}
                    </td>
                    <td className="py-2 pr-3 text-on-surface">
                      {r.current ? (<>{r.current.task?.title ?? 'Sin tarea concreta'}<span className="block text-[11px] text-on-surface-variant">{r.current.project?.name}</span></>)
                        : r.previous ? (<>{r.previous.task?.title ?? 'Sin tarea concreta'}<span className="block text-[11px] text-on-surface-variant">{r.previous.project?.name} · última tarea</span></>)
                          : <span className="text-on-surface-variant">Sin actividad reciente</span>}
                    </td>
                    <td className="py-2 pr-3 text-on-surface-variant">{r.previous?.result ?? '—'}</td>
                    <td className="py-2 text-on-surface">
                      {r.next ? (<>{r.next.task.title}<span className="block text-[11px] text-on-surface-variant">{r.next.project.name}</span></>) : <span className="text-on-surface-variant">Sin tarea lista</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      {data?.note && <p className="mt-2 text-[11px] text-outline">{data.note}</p>}
    </section>
  );
}
